import { useEffect, useState } from "react";
import { api, formatIDR, formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, BedDouble, Check, X } from "lucide-react";

// ---------- Perhitungan lokal (identik dengan backend _compute_room_class) ----------
export function computeRoomClass(c) {
    const f = (k) => Number(c[k]) || 0;
    const effDepBuilding =
        f("building_area_m2") > 0 && f("building_rate_per_m2") > 0
            ? f("building_area_m2") * f("building_rate_per_m2")
            : f("dep_building");
    const effSalary =
        f("nurse_count") > 0 && f("nurse_salary") > 0
            ? f("nurse_count") * f("nurse_salary")
            : f("salary_nurse");
    const effLaundry =
        f("laundry_kg") > 0 && f("laundry_rate_per_kg") > 0
            ? f("laundry_kg") * f("laundry_rate_per_kg")
            : f("laundry");
    const directMonthly =
        effDepBuilding + f("dep_medical") + f("dep_nonmedical") + effSalary + f("bhp_nonmedis") + f("gizi");
    const overheadMonthly = effLaundry + f("utilities") + f("overhead_mgmt");
    const capacity = Math.floor((f("beds") * 365 * f("bor_percent")) / 100);
    const directAnnual = directMonthly * 12;
    const overheadAnnual = overheadMonthly * 12;
    const totalAnnual = directAnnual + overheadAnnual;
    return {
        effDepBuilding,
        effSalary,
        effLaundry,
        directMonthly,
        overheadMonthly,
        capacity,
        directAnnual,
        overheadAnnual,
        totalAnnual,
        nurseRatio: f("nurse_count") > 0 ? f("beds") / f("nurse_count") : 0,
        nuc: capacity > 0 ? totalAnnual / capacity : 0,
    };
}

const emptyClass = (name) => ({
    id: `local-${Date.now()}`,
    name,
    beds: 0,
    bor_percent: 75,
    building_area_m2: 0,
    building_rate_per_m2: 0,
    dep_building: 0,
    dep_medical: 0,
    dep_nonmedical: 0,
    nurse_count: 0,
    nurse_salary: 0,
    salary_nurse: 0,
    bhp_nonmedis: 0,
    gizi: 0,
    laundry_kg: 0,
    laundry_rate_per_kg: 0,
    laundry: 0,
    utilities: 0,
    overhead_mgmt: 0,
});

function StepSection({ step, title, description, children }) {
    return (
        <div className="surface-card p-6">
            <div className="mb-4 flex items-start gap-3">
                <div className="w-8 h-8 shrink-0 rounded-full bg-[#245B4B] text-white flex items-center justify-center font-heading font-semibold text-sm">
                    {step}
                </div>
                <div>
                    <h3 className="font-heading text-lg font-medium">{title}</h3>
                    {description && (
                        <p className="text-sm text-[#4A5E57] mt-1 leading-relaxed">{description}</p>
                    )}
                </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">{children}</div>
        </div>
    );
}

function NumField({ label, value, onChange, testid, suffix, help }) {
    return (
        <div>
            <Label className="text-[#1A362D]">{label}</Label>
            <div className="relative mt-1">
                <Input
                    type="number"
                    value={value}
                    data-testid={testid}
                    onChange={(e) => onChange(e.target.value)}
                    className="pr-16"
                />
                {suffix && (
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-[#6A9C89] uppercase tracking-widest">
                        {suffix}
                    </span>
                )}
            </div>
            {help && <div className="text-xs text-[#6A9C89] mt-1">{help}</div>}
        </div>
    );
}

// Blok "kalkulator bantu": 2 input pengali ATAU nominal langsung; nilai efektif ditampilkan
function HelperCost({ title, hint, a, b, direct, effective, testidPrefix }) {
    return (
        <div className="col-span-1 md:col-span-2 rounded-md border border-[#E2E9E5] p-4">
            <div className="text-sm font-medium text-[#1A362D] mb-1">{title}</div>
            {hint && <div className="text-xs text-[#6A9C89] mb-3">{hint}</div>}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
                <NumField {...a} testid={`${testidPrefix}-a`} />
                <NumField {...b} testid={`${testidPrefix}-b`} />
                <NumField
                    {...direct}
                    testid={`${testidPrefix}-direct`}
                    label={`${direct.label}`}
                />
            </div>
            <div
                className="mt-3 rounded-md bg-[#F7F9F6] border border-[#D1DCD8] px-3 py-2 flex items-baseline justify-between"
                data-testid={`${testidPrefix}-effective`}
            >
                <span className="text-xs text-[#6A9C89] uppercase tracking-widest">
                    Nilai dipakai / bulan
                </span>
                <span className="font-heading font-medium text-[#1A362D]">
                    {formatIDR(effective)}
                </span>
            </div>
        </div>
    );
}

function ResultRow({ label, value, testid, bold }) {
    return (
        <div className="flex justify-between text-sm">
            <span className="text-[#4A5E57]">{label}</span>
            <span className={bold ? "font-semibold" : "font-medium"} data-testid={testid}>
                {formatIDR(value)}
            </span>
        </div>
    );
}

export default function RoomUnitCost() {
    const [classes, setClasses] = useState([]);
    const [selectedId, setSelectedId] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [applying, setApplying] = useState(false);
    const [editingName, setEditingName] = useState(null); // {id, value}

    useEffect(() => {
        api.get("/room-unit-cost")
            .then((res) => {
                const cls = res.data.params.classes || [];
                setClasses(cls);
                setSelectedId(cls[0]?.id || null);
            })
            .catch((e) => toast.error(formatApiError(e)))
            .finally(() => setLoading(false));
    }, []);

    const sel = classes.find((c) => c.id === selectedId) || classes[0];
    const r = sel ? computeRoomClass(sel) : null;

    function updateSel(field, value) {
        setClasses((cs) =>
            cs.map((c) => (c.id === sel.id ? { ...c, [field]: value } : c))
        );
    }

    function addClass() {
        const name = window.prompt("Nama kelas kamar baru (mis. SVIP, Isolasi):");
        if (!name || !name.trim()) return;
        const nc = emptyClass(name.trim());
        setClasses((cs) => [...cs, nc]);
        setSelectedId(nc.id);
    }

    function removeClass(c) {
        if (classes.length <= 1) {
            toast.error("Minimal harus ada satu kelas kamar");
            return;
        }
        if (!window.confirm(`Hapus kelas "${c.name}" beserta datanya?`)) return;
        setClasses((cs) => {
            const next = cs.filter((x) => x.id !== c.id);
            if (selectedId === c.id) setSelectedId(next[0]?.id || null);
            return next;
        });
    }

    function commitRename() {
        if (!editingName) return;
        const v = editingName.value.trim();
        if (v) {
            setClasses((cs) =>
                cs.map((c) => (c.id === editingName.id ? { ...c, name: v } : c))
            );
        }
        setEditingName(null);
    }

    async function save() {
        setSaving(true);
        try {
            const payload = {
                classes: classes.map((c) => {
                    const out = {};
                    for (const [k, v] of Object.entries(c)) {
                        out[k] = k === "id" || k === "name" ? v : Number(v) || 0;
                    }
                    if (String(out.id).startsWith("local-")) delete out.id;
                    return out;
                }),
            };
            const res = await api.put("/room-unit-cost", payload);
            const cls = res.data.params.classes;
            setClasses(cls);
            if (!cls.find((c) => c.id === selectedId)) setSelectedId(cls[0]?.id);
            toast.success("Unit cost kamar tersimpan");
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setSaving(false);
        }
    }

    async function applyToMaster() {
        setApplying(true);
        try {
            // Simpan dulu agar NUC yang diterapkan sesuai input terkini
            const payload = {
                classes: classes.map((c) => {
                    const out = {};
                    for (const [k, v] of Object.entries(c)) {
                        out[k] = k === "id" || k === "name" ? v : Number(v) || 0;
                    }
                    if (String(out.id).startsWith("local-")) delete out.id;
                    return out;
                }),
            };
            const saved = await api.put("/room-unit-cost", payload);
            setClasses(saved.data.params.classes);
            const res = await api.post("/room-unit-cost/apply-to-master");
            toast.success(
                `Master Fasilitas diperbarui: ${res.data.created} dibuat, ${res.data.updated} diperbarui`
            );
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setApplying(false);
        }
    }

    if (loading) return <div className="text-[#4A5E57]">Memuat...</div>;
    if (!sel) return null;

    return (
        <div className="space-y-6" data-testid="room-uc-page">
            {/* Pemilih kelas kamar */}
            <div className="surface-card p-4">
                <div className="text-xs text-[#6A9C89] uppercase tracking-widest mb-2">
                    Kelas Kamar
                </div>
                <div className="flex flex-wrap items-center gap-2">
                    {classes.map((c) => {
                        const active = c.id === sel.id;
                        const filled = computeRoomClass(c).nuc > 0;
                        return editingName?.id === c.id ? (
                            <div key={c.id} className="flex items-center gap-1">
                                <Input
                                    autoFocus
                                    value={editingName.value}
                                    data-testid="ruc-rename-input"
                                    onChange={(e) =>
                                        setEditingName({ id: c.id, value: e.target.value })
                                    }
                                    onKeyDown={(e) => e.key === "Enter" && commitRename()}
                                    className="h-8 w-36"
                                />
                                <button
                                    onClick={commitRename}
                                    data-testid="ruc-rename-save"
                                    className="p-1.5 rounded text-[#245B4B] hover:bg-[#E9EFEA]"
                                >
                                    <Check size={15} />
                                </button>
                                <button
                                    onClick={() => setEditingName(null)}
                                    className="p-1.5 rounded text-[#4A5E57] hover:bg-[#E9EFEA]"
                                >
                                    <X size={15} />
                                </button>
                            </div>
                        ) : (
                            <div
                                key={c.id}
                                className={`group flex items-center gap-1 rounded-full border px-3 py-1.5 text-sm cursor-pointer transition-colors ${
                                    active
                                        ? "bg-[#245B4B] border-[#245B4B] text-white"
                                        : "border-[#D1DCD8] text-[#4A5E57] hover:border-[#6A9C89]"
                                }`}
                                data-testid={`ruc-class-chip-${c.name.toLowerCase().replace(/\s+/g, "-")}`}
                                onClick={() => setSelectedId(c.id)}
                            >
                                <BedDouble size={14} />
                                <span>{c.name}</span>
                                {filled && (
                                    <span
                                        className={`w-1.5 h-1.5 rounded-full ${active ? "bg-white" : "bg-[#6A9C89]"}`}
                                    />
                                )}
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setEditingName({ id: c.id, value: c.name });
                                    }}
                                    title="Ubah nama"
                                    data-testid={`ruc-rename-btn-${c.name.toLowerCase().replace(/\s+/g, "-")}`}
                                    className={`p-0.5 rounded opacity-0 group-hover:opacity-100 transition-opacity ${
                                        active ? "hover:bg-white/20" : "hover:bg-[#E9EFEA]"
                                    }`}
                                >
                                    <Pencil size={12} />
                                </button>
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        removeClass(c);
                                    }}
                                    title="Hapus kelas"
                                    data-testid={`ruc-delete-btn-${c.name.toLowerCase().replace(/\s+/g, "-")}`}
                                    className={`p-0.5 rounded opacity-0 group-hover:opacity-100 transition-opacity ${
                                        active ? "hover:bg-white/20" : "hover:bg-[#E9EFEA] text-[#B3432B]"
                                    }`}
                                >
                                    <Trash2 size={12} />
                                </button>
                            </div>
                        );
                    })}
                    <button
                        onClick={addClass}
                        data-testid="ruc-add-class-btn"
                        className="flex items-center gap-1 rounded-full border border-dashed border-[#6A9C89] px-3 py-1.5 text-sm text-[#245B4B] hover:bg-[#E9EFEA] transition-colors"
                    >
                        <Plus size={14} /> Tambah Kelas
                    </button>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="lg:col-span-2 space-y-6">
                    <StepSection
                        step={1}
                        title="Kapasitas Normatif"
                        description="Gunakan target BOR ideal (75–80%), bukan pasien aktual, agar unit cost tidak melambung saat kamar sepi. Kapasitas = Bed × 365 × BOR."
                    >
                        <NumField
                            label={`Jumlah Tempat Tidur (${sel.name})`}
                            value={sel.beds}
                            onChange={(v) => updateSel("beds", v)}
                            testid="ruc-beds"
                            suffix="bed"
                        />
                        <NumField
                            label="Target BOR"
                            value={sel.bor_percent}
                            onChange={(v) => updateSel("bor_percent", v)}
                            testid="ruc-bor"
                            suffix="%"
                            help="Bed Occupancy Rate ideal, umumnya 75–80%"
                        />
                        <div
                            className="col-span-1 md:col-span-2 rounded-md bg-[#E9EFEA] px-4 py-3 flex items-baseline justify-between"
                            data-testid="ruc-capacity"
                        >
                            <span className="text-sm text-[#245B4B]">
                                Kapasitas = {Number(sel.beds) || 0} bed × 365 hari ×{" "}
                                {Number(sel.bor_percent) || 0}%
                            </span>
                            <span className="font-heading text-lg font-semibold text-[#1A362D]">
                                {r.capacity.toLocaleString("id-ID")} hari rawat/tahun
                            </span>
                        </div>
                    </StepSection>

                    <StepSection
                        step={2}
                        title="Biaya Langsung per Bulan (Direct Cost)"
                        description={`Biaya yang nyata & eksklusif dipakai kelas ${sel.name}. Isi kalkulator bantu (otomatis mengalikan) ATAU langsung nominal per bulan.`}
                    >
                        <HelperCost
                            title="Penyusutan Gedung"
                            hint="Berdasarkan luas lantai (m²). Kamar VIP memakan m² per bed lebih besar dibanding Kelas 3."
                            testidPrefix="ruc-depb"
                            a={{
                                label: "Luas Lantai",
                                suffix: "m²",
                                value: sel.building_area_m2,
                                onChange: (v) => updateSel("building_area_m2", v),
                            }}
                            b={{
                                label: "Penyusutan per m²/bulan",
                                suffix: "Rp",
                                value: sel.building_rate_per_m2,
                                onChange: (v) => updateSel("building_rate_per_m2", v),
                            }}
                            direct={{
                                label: "atau Nominal Langsung/bulan",
                                suffix: "Rp",
                                value: sel.dep_building,
                                onChange: (v) => updateSel("dep_building", v),
                            }}
                            effective={r.effDepBuilding}
                        />
                        <NumField
                            label="Penyusutan Alat Medis / bulan"
                            value={sel.dep_medical}
                            onChange={(v) => updateSel("dep_medical", v)}
                            testid="ruc-dep-medical"
                            suffix="Rp"
                            help="Bed elektrik/manual, monitor pasien, tiang infus, dll"
                        />
                        <NumField
                            label="Penyusutan Alat Non-Medis / bulan"
                            value={sel.dep_nonmedical}
                            onChange={(v) => updateSel("dep_nonmedical", v)}
                            testid="ruc-dep-nonmedical"
                            suffix="Rp"
                            help="AC, TV, kulkas, sofa penunggu (umumnya VIP/Kelas 1)"
                        />
                        <HelperCost
                            title="Gaji & Tunjangan Perawat"
                            hint={`Rasio perawat : pasien berbeda tiap kelas (VIP ±1:3, Kelas 3 ±1:8).${
                                r.nurseRatio > 0
                                    ? ` Rasio saat ini 1 perawat : ${r.nurseRatio.toLocaleString("id-ID", { maximumFractionDigits: 1 })} bed.`
                                    : ""
                            }`}
                            testidPrefix="ruc-nurse"
                            a={{
                                label: "Jumlah Perawat",
                                suffix: "org",
                                value: sel.nurse_count,
                                onChange: (v) => updateSel("nurse_count", v),
                            }}
                            b={{
                                label: "Gaji per Perawat/bulan",
                                suffix: "Rp",
                                value: sel.nurse_salary,
                                onChange: (v) => updateSel("nurse_salary", v),
                            }}
                            direct={{
                                label: "atau Nominal Langsung/bulan",
                                suffix: "Rp",
                                value: sel.salary_nurse,
                                onChange: (v) => updateSel("salary_nurse", v),
                            }}
                            effective={r.effSalary}
                        />
                        <NumField
                            label="BHP Non-Medis / bulan"
                            value={sel.bhp_nonmedis}
                            onChange={(v) => updateSel("bhp_nonmedis", v)}
                            testid="ruc-bhp"
                            suffix="Rp"
                            help="Sabun, tisu, amenities kamar mandi per kamar"
                        />
                        <NumField
                            label="Gizi / Makan Pasien / bulan"
                            value={sel.gizi}
                            onChange={(v) => updateSel("gizi", v)}
                            testid="ruc-gizi"
                            suffix="Rp"
                            help="Standar menu & snack harian berbeda antar kelas"
                        />
                    </StepSection>

                    <StepSection
                        step={3}
                        title="Alokasi Overhead per Bulan (Biaya Tidak Langsung)"
                        description={`Biaya unit penunjang yang dibebankan ke kelas ${sel.name} memakai cost driver yang tepat.`}
                    >
                        <HelperCost
                            title="Laundry (Cuci Linen)"
                            hint="Cost driver: berat cucian (kg). VIP mengganti linen lebih sering + selimut/handuk penunggu."
                            testidPrefix="ruc-laundry"
                            a={{
                                label: "Berat Cucian/bulan",
                                suffix: "kg",
                                value: sel.laundry_kg,
                                onChange: (v) => updateSel("laundry_kg", v),
                            }}
                            b={{
                                label: "Tarif per kg",
                                suffix: "Rp",
                                value: sel.laundry_rate_per_kg,
                                onChange: (v) => updateSel("laundry_rate_per_kg", v),
                            }}
                            direct={{
                                label: "atau Nominal Langsung/bulan",
                                suffix: "Rp",
                                value: sel.laundry,
                                onChange: (v) => updateSel("laundry", v),
                            }}
                            effective={r.effLaundry}
                        />
                        <NumField
                            label="Listrik & Air / bulan"
                            value={sel.utilities}
                            onChange={(v) => updateSel("utilities", v)}
                            testid="ruc-utilities"
                            suffix="Rp"
                            help="Alokasi berdasarkan luas lantai, titik lampu/AC, atau kapasitas watt"
                        />
                        <NumField
                            label="Manajemen, IT, Keamanan, CS / bulan"
                            value={sel.overhead_mgmt}
                            onChange={(v) => updateSel("overhead_mgmt", v)}
                            testid="ruc-mgmt"
                            suffix="Rp"
                            help="Alokasi berdasarkan luas bangunan / jumlah pegawai kelas ini"
                        />
                    </StepSection>
                </div>

                {/* Langkah 4 — Hasil */}
                <div>
                    <div className="surface-card p-6 sticky top-6" data-testid="ruc-result-panel">
                        <div className="flex items-center gap-2 mb-1">
                            <div className="w-7 h-7 rounded-full bg-[#245B4B] text-white flex items-center justify-center font-heading font-semibold text-xs">
                                4
                            </div>
                            <h3 className="font-heading text-lg font-medium">
                                Hasil — {sel.name}
                            </h3>
                        </div>
                        <p className="text-xs text-[#6A9C89] mb-4">
                            Normative Unit Cost = Total Biaya Setahun ÷ Kapasitas Normatif
                        </p>
                        <div className="rounded-md bg-[#245B4B] text-white px-4 py-4 mb-4">
                            <div className="text-[11px] uppercase tracking-widest text-white/70">
                                Unit Cost per Hari Rawat (HPP)
                            </div>
                            <div
                                className="font-heading text-3xl font-semibold mt-1"
                                data-testid="ruc-nuc"
                            >
                                {formatIDR(r.nuc)}
                            </div>
                            <div className="text-xs text-white/70 mt-1">
                                Menjual di bawah angka ini berarti rugi.
                            </div>
                        </div>
                        <div className="space-y-2">
                            <ResultRow
                                label="Biaya Langsung / tahun"
                                value={r.directAnnual}
                                testid="ruc-direct-annual"
                            />
                            <ResultRow
                                label="Overhead / tahun"
                                value={r.overheadAnnual}
                                testid="ruc-overhead-annual"
                            />
                            <ResultRow
                                label="Total Biaya / tahun"
                                value={r.totalAnnual}
                                testid="ruc-total-annual"
                                bold
                            />
                            <div className="flex justify-between text-sm">
                                <span className="text-[#4A5E57]">Kapasitas Normatif</span>
                                <span className="font-medium" data-testid="ruc-capacity-result">
                                    {r.capacity.toLocaleString("id-ID")} hari rawat
                                </span>
                            </div>
                        </div>
                        <Button
                            onClick={save}
                            disabled={saving}
                            data-testid="ruc-save-btn"
                            className="w-full mt-5 bg-[#245B4B] hover:bg-[#1A4236] text-white"
                        >
                            {saving ? "Menyimpan..." : "Simpan Unit Cost Kamar"}
                        </Button>
                        <Button
                            onClick={applyToMaster}
                            disabled={applying}
                            variant="outline"
                            data-testid="ruc-apply-to-master-btn"
                            className="w-full mt-2 border-[#D1DCD8] text-[#245B4B] hover:bg-[#E9EFEA]"
                        >
                            {applying ? "Menerapkan..." : "Terapkan ke Master Fasilitas"}
                        </Button>
                        <div className="text-xs text-[#6A9C89] mt-2">
                            Membuat/memperbarui item "Kamar {sel.name} (per Hari Rawat)" di Master
                            Fasilitas dengan Unit Cost Dasar = NUC, sehingga kamar bisa dipilih di
                            paket.
                        </div>
                    </div>
                </div>
            </div>

            {/* Ringkasan semua kelas */}
            <div className="surface-card p-6" data-testid="ruc-summary">
                <h3 className="font-heading text-lg font-medium mb-1">Ringkasan Semua Kelas</h3>
                <p className="text-sm text-[#4A5E57] mb-4">
                    Perbandingan HPP per hari rawat seluruh kelas kamar.
                </p>
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="text-left text-xs text-[#6A9C89] uppercase tracking-widest border-b border-[#D1DCD8]">
                                <th className="py-2 pr-3">Kelas</th>
                                <th className="py-2 pr-3 text-right">Bed</th>
                                <th className="py-2 pr-3 text-right">BOR</th>
                                <th className="py-2 pr-3 text-right">Kapasitas/thn</th>
                                <th className="py-2 pr-3 text-right">Langsung/thn</th>
                                <th className="py-2 pr-3 text-right">Overhead/thn</th>
                                <th className="py-2 text-right">NUC/Hari Rawat</th>
                            </tr>
                        </thead>
                        <tbody>
                            {classes.map((c) => {
                                const rc = computeRoomClass(c);
                                return (
                                    <tr
                                        key={c.id}
                                        data-testid={`ruc-summary-row-${c.name.toLowerCase().replace(/\s+/g, "-")}`}
                                        className={`border-b border-[#F0F4F1] cursor-pointer hover:bg-[#F7F9F6] ${
                                            c.id === sel.id ? "bg-[#E9EFEA]/60" : ""
                                        }`}
                                        onClick={() => setSelectedId(c.id)}
                                    >
                                        <td className="py-2.5 pr-3 font-medium">{c.name}</td>
                                        <td className="py-2.5 pr-3 text-right">
                                            {Number(c.beds) || 0}
                                        </td>
                                        <td className="py-2.5 pr-3 text-right">
                                            {Number(c.bor_percent) || 0}%
                                        </td>
                                        <td className="py-2.5 pr-3 text-right">
                                            {rc.capacity.toLocaleString("id-ID")}
                                        </td>
                                        <td className="py-2.5 pr-3 text-right">
                                            {formatIDR(rc.directAnnual)}
                                        </td>
                                        <td className="py-2.5 pr-3 text-right">
                                            {formatIDR(rc.overheadAnnual)}
                                        </td>
                                        <td className="py-2.5 text-right font-semibold text-[#245B4B]">
                                            {formatIDR(rc.nuc)}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}
